import { IngredientCategory, IngredientCategoryData } from './index';
import { ReadCollectionGql } from './gql';

type Data = {
  documents: IngredientCategoryData[];
  nextToken: string;
};

class IngredientCategoryCollection {

  readonly businessId: string;
  readonly documents: IngredientCategory[];
  readonly nextToken: string;

  constructor(businessId: string = '', data: Data = { documents: [], nextToken: '' }) {
    this.businessId = businessId;
    this.documents = data.documents.map(it => new IngredientCategory(it));
    this.nextToken = data.nextToken;
  }

  async readAll(): Promise<this> {
    let collection = this;
    while (collection.nextToken) {
      const { businessId, nextToken } = collection;
      if (!businessId) continue;
      const gql = await new ReadCollectionGql().fetch({ businessId, nextToken });
      if (!gql.result) throw new Error('invalid result');
      collection = collection.merge(new IngredientCategoryCollection(businessId, gql.result));
    }
    return collection;
  }

  merge(collection: IngredientCategoryCollection): this {
    const documents = IngredientCategory.sort([...this.documents, ...collection.documents]);
    const nextToken = collection.nextToken;
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents, nextToken });
  }

  add(category: IngredientCategory): this {
    const documents = IngredientCategory.sort([...this.documents, category]);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  replace(category: IngredientCategory): this {
    const documents = IngredientCategory.sort(this.documents.map(it => it.id === category.id ? category : it));
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  remove(category: IngredientCategory): this {
    const documents = this.documents.filter(it => it.id !== category.id);
    return Object.assign(Object.create(this.constructor.prototype), { ...this, documents });
  }

  toJSON(): Data {
    const { nextToken } = this;
    const documents = this.documents.map(it => it.toJSON());
    return { documents, nextToken };
  }

  static async read(businessId: string): Promise<IngredientCategoryCollection> {
    const gql = await new ReadCollectionGql().fetch({ businessId });
    if (!gql.result) throw new Error('invalid result');
    return new IngredientCategoryCollection(businessId, gql.result);
  }

}

export { IngredientCategoryCollection };
export type { Data as IngredientCategoryCollectionData };